import React, { useState, useEffect } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { SuccessNotification, ErrorNotification } from '../utils'
import api, { BASE_URL } from '../utils/api'
import { SlantedWrapper, SVGLogo } from '../components'
import { RiEyeLine } from 'react-icons/ri'
import { FaRegEyeSlash } from 'react-icons/fa'

const ActivityUserCompleteRegistration = () => {
  const location = useLocation()
  const history = useHistory()
  const [submitting, setSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formData, setFormData] = useState({
    invitation_token: '',
    password: '',
    password_confirm: '',
    firstname: '',
    lastname: '',
    username: '',
    phone: '',
    gender: 'Male',
    dob: '',
    country: '',
    region: '',
    district: '',
    community: '',
    photo_url: ''
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    // Get token from URL path parameters
    const token = location.pathname.split('/').pop()

    if (token && token !== 'complete-managed-invite') {
      setFormData(prev => ({
        ...prev,
        invitation_token: token
      }))
    } else {
      ErrorNotification('Invalid invitation link')
      history.push('/')
    }
  }, [location.pathname, history])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }

    if (!formData.password_confirm.trim()) {
      newErrors.password_confirm = 'Please confirm your password'
    } else if (formData.password !== formData.password_confirm) {
      newErrors.password_confirm = 'Passwords do not match'
    }

    if (!formData.firstname.trim()) {
      newErrors.firstname = 'First name is required'
    }

    if (!formData.lastname.trim()) {
      newErrors.lastname = 'Last name is required'
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setSubmitting(true)
      // await api.post('managed-users/complete_managed_user_setup/', formData)
      await fetch(`${BASE_URL}managed-users/complete_managed_user_setup/`, {
        method: 'POST',
        body: JSON.stringify(formData),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      SuccessNotification('Registration completed successfully! You can now log in.')
      history.push('/')
    } catch (error) {
      console.error('Error completing registration:', error)
      ErrorNotification(error?.response?.data?.message || 'Failed to complete registration')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen w-full grid grid-cols-1 md:grid-cols-2 bg-white dark:bg-dark">
      {/* Left: Registration Form */}
      <div className="flex flex-col justify-center items-center px-4 md:px-0 min-h-screen">
        <div className="w-full max-w-sm mx-auto flex flex-col justify-center py-8 max-h-[calc(100vh-40px)] overflow-y-auto">
          {/* Logo and Brand */}
          <div className="flex items-center justify-center mb-6">
            <span className="inline-flex items-center gap-3">
              <SVGLogo />
              <span className="text-2xl font-bold" style={{ color: '#FDBA2F' }}>FarmerKonnect</span>
            </span>
          </div>

          {/* Heading */}
          <h2 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-1 text-center">Complete Registration</h2>
          <p className="text-gray-500 text-center mb-7">Please complete your profile to access the system.</p>

          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            {/* Personal Information */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstname"
                  value={formData.firstname}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  className={`w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400 ${errors.firstname ? 'border-red-500' : 'border-gray-300'
                    }`}
                  disabled={submitting}
                />
                {errors.firstname && (
                  <div className="text-red-500 text-xs mt-1">{errors.firstname}</div>
                )}
              </div>

              <div>
                <label className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  className={`w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400 ${errors.lastname ? 'border-red-500' : 'border-gray-300'
                    }`}
                  disabled={submitting}
                />
                {errors.lastname && (
                  <div className="text-red-500 text-xs mt-1">{errors.lastname}</div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter your phone number"
                className={`w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400 ${errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                disabled={submitting}
              />
              {errors.phone && (
                <div className="text-red-500 text-xs mt-1">{errors.phone}</div>
              )}
            </div>

            <div>
              <label className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className={`w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent ${errors.gender ? 'border-red-500' : 'border-gray-300'
                  }`}
                disabled={submitting}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {errors.gender && (
                <div className="text-red-500 text-xs mt-1">{errors.gender}</div>
              )}
            </div>

            <div>
              <label className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                Date of Birth
              </label>
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleInputChange}
                className="w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
                disabled={submitting}
              />
            </div>

            {/* Password Fields */}
            <div>
              <label className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  className={`w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400 ${errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="absolute right-2 top-3.5 text-gray-400 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <FaRegEyeSlash /> : <RiEyeLine />}
                </button>
              </div>
              {errors.password && (
                <div className="text-red-500 text-xs mt-1">{errors.password}</div>
              )}
            </div>

            <div>
              <label className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="password_confirm"
                  value={formData.password_confirm}
                  onChange={handleInputChange}
                  placeholder="Confirm your password"
                  className={`w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400 ${errors.password_confirm ? 'border-red-500' : 'border-gray-300'
                    }`}
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="absolute right-2 top-3.5 text-gray-400 hover:text-gray-700"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <FaRegEyeSlash /> : <RiEyeLine />}
                </button>
              </div>
              {errors.password_confirm && (
                <div className="text-red-500 text-xs mt-1">{errors.password_confirm}</div>
              )}
            </div>

            {/* Location Fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="Enter your country"
                  className="w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                  Region
                </label>
                <input
                  type="text"
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  placeholder="Enter your region"
                  className="w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                  District
                </label>
                <input
                  type="text"
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  placeholder="Enter your district"
                  className="w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="block text-base font-medium text-gray-700 mb-1 dark:text-white">
                  Community
                </label>
                <input
                  type="text"
                  name="community"
                  value={formData.community}
                  onChange={handleInputChange}
                  placeholder="Enter your community"
                  className="w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                  disabled={submitting}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-yellowcolor hover:bg-yellowcolor/90 text-black font-normal py-3 rounded-md text-base transition-colors duration-200 mb-2 shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Completing Registration..." : "Complete Registration"}
            </button>

            <div className="text-center font-normal text-gray-500 mt-2">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => history.push('/')}
                className="text-yellowcolor hover:underline font-medium"
              >
                Sign in
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Right: Image */}
      <SlantedWrapper />
    </div>
  )
}

export default ActivityUserCompleteRegistration
